import * as React from "react";
import * as RB from "react-bootstrap";
import Select from "react-select";
import Feedback from "react-bootstrap/Feedback";
import styled from "styled-components";
import { getErrorMessage } from "../../../lib/error";
import { ErrorObject } from "../../../../../server/types/error";
import { FormControlProps } from "react-bootstrap";
import { decimalCalc } from "../../../../../server/lib/aggregate/common";

interface PillProp {
	variant: "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "light" | "dark";
	text: string;
}

const LabelWrap = styled.div`
	white-space: pre;
`;
export type InputRowProps = {
	name: string;
	rowName: React.ReactNode;
	children: React.ReactNode;
	pill?: PillProp;
	errors?: ErrorObject;
	subText?: React.ReactNode;
};
export const InputRow = (props: InputRowProps) => {
	const { pill, errors, rowName, name, children, subText } = props;
	return (
		<RB.Row className="m-2">
			<RB.Col lg={4} className="text-end">
				<LabelWrap>
					{rowName}
					{pill && (
						<RB.Badge pill bg={pill.variant}>
							{pill.text}
						</RB.Badge>
					)}
				</LabelWrap>
			</RB.Col>
			<RB.Col>
				{children}
				{!!errors && !!errors[name] && (
					<Feedback type={"invalid"} style={{ display: "block" }}>
						{getErrorMessage(name, errors)}
					</Feedback>
				)}
			</RB.Col>
			{subText && <RB.Col>{subText}</RB.Col>}
		</RB.Row>
	);
};

export interface RowProp extends FormControlProps {
	value: string;
	onChange: (value: any) => void;
	name: string;
	rowName: React.ReactNode;
	id: string;
	pill?: PillProp;
	type?: string;
	errors?: ErrorObject;
	subText?: React.ReactNode;
}
export const TextRow = (props: RowProp) => {
	const { value, onChange, name, errors, as, disabled, ...attr } = props;
	return (
		<InputRow {...attr} name={name} errors={errors}>
			<>
				{props.type === "number" && (
					<RB.Form.Control
						type={"text"}
						value={value === undefined ? "" : value}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							const tmp = Number(e.target.value);
							if (isNaN(tmp)) return;
							onChange(Math.floor(tmp));
						}}
						pattern="\d+"
						min={1}
						disabled={disabled}
						isInvalid={!!errors && !!errors[name]}
					/>
				)}

				{props.type === "decimal" && <NumberInput {...props} />}
				{props.type !== "number" && props.type !== "decimal" && (
					<RB.Form.Control
						as={as}
						type={"text"}
						value={value}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
						disabled={disabled}
						isInvalid={!!errors && !!errors[name]}
					/>
				)}
			</>
		</InputRow>
	);
};

export interface NumberRowProp extends RowProp {
	step?: string;
	min?: number;
}
export const NumberInput = React.memo<NumberRowProp>((props) => {
	const {
		value,
		onChange,
		name,
		errors,
		as,
		disabled,
		style,
		step = "0.1",
		min = 1,
		isInvalid = false,
		..._attr
	} = props;
	const [viewValue, setViewValue] = React.useState<string | number>(value);
	const handleChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const v = e.target.value;
			if (!v) {
				setViewValue(v);
				return onChange(undefined);
			}
			if (/^-d+\.$/.test(v)) return setViewValue(v);

			const roundValue = decimalCalc(Number(v), 1 / Number(step));
			setViewValue(roundValue);
			onChange(roundValue);
		},
		[onChange, step],
	);
	React.useEffect(() => {
		if (value) return;
		setViewValue("");
	}, [value]);
	return (
		<RB.Form.Control
			type={"number"}
			as={as}
			value={viewValue}
			onChange={handleChange}
			step={step}
			min={min}
			isInvalid={isInvalid || (!!errors && !!errors[name])}
			disabled={disabled}
			style={style}
		/>
	);
});

export type SelectRowProps = Omit<RowProp, "value"> & {
	value: string | number;
	options: { label: string; value: string | number }[];
};

export const SelectRow = (props: SelectRowProps) => {
	const { name, options, value, onChange, errors, ...other } = props;
	const selectbox = () => {
		return (
			<RB.Form.Select
				value={value ? value.toString() : ""}
				onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
					onChange(e.target.value);
				}}
				isInvalid={!!errors && !!errors[name]}
			>
				<option></option>
				{options.map(({ value, label }) => {
					return (
						<option key={value} value={value}>
							{label}
						</option>
					);
				})}
			</RB.Form.Select>
		);
	};
	return (
		<InputRow name={name} {...other} errors={errors}>
			{selectbox()}
		</InputRow>
	);
};

export const Wrap = styled.div`
	& div.invalid {
		& div {
			border-color: red;
		}
	}
`;
export type ReactMultiSelectRowProps = Omit<SelectRowProps, "value"> & { values: (string | number)[] };
export const ReactMultiSelectRow = (props: ReactMultiSelectRowProps) => {
	const { options, onChange, values, errors, name, ...other } = props;
	const selected = React.useMemo<{ label: string; value: string | number }[]>(() => {
		if (!values) return [];
		return options.filter((op) => values.includes(op.value));
	}, [options, values]);
	const onReactSelectChange = React.useCallback(
		(selectedOptions: { label: string; value: string | number }[]) =>
			onChange(selectedOptions ? selectedOptions.map((op) => op.value) : []),
		[onChange],
	);
	return (
		<InputRow {...other} name={name} errors={errors}>
			<Wrap>
				<Select
					className={"w-100" + (!!errors && !!errors[name] ? " invalid" : "")}
					onChange={onReactSelectChange}
					options={options as any}
					value={selected}
					isMulti
					closeMenuOnSelect={false}
				/>
			</Wrap>
		</InputRow>
	);
};

export const CheckBoxRow = (props: SelectRowProps) => {
	//const [checked, setChecked] = React.useState<{ label: string; value: string }>();
	//const onClick = React.useCallback(() => {});
	const { options, ...other } = props;
	React.useEffect(() => {
		console.log(options[0].label);
	});
	const checkbox = () => {
		return (
			<>
				{options.map((option) => {
					return <RB.Form.Check key={option.value} type="checkbox" {...option} id={other.id + option.value} />;
				})}
			</>
		);
	};
	return <InputRow {...other}>{checkbox()}</InputRow>;
};

export type SelectRangeRowProps = Omit<Omit<SelectRowProps, "value">, "options"> & {
	values: {
		from: number;
		to: number;
	};
	toOptions: { label: string; value: number }[];
	fromOptions: { label: string; value: number }[];
};
export const SelectRangeRow = (props: SelectRangeRowProps) => {
	const { toOptions, fromOptions, values, onChange, name, errors, ...other } = props;
	const onChangeSelect = React.useCallback(
		(name: "from" | "to", value: string) => {
			const tmp = Number(value);
			const v = !!value && !isNaN(tmp) ? tmp : undefined;
			onChange({ ...values, [name]: v });
		},
		[onChange, values],
	);
	const toFilterOption = React.useMemo(() => {
		if (!values || !values.from) return toOptions;
		return toOptions.filter((op) => op.value >= values.from);
	}, [toOptions, values]);
	const myError = React.useMemo<ErrorObject>(() => {
		if (!errors || !errors[name]) return undefined;
		const tmp = errors[name];
		if (typeof tmp === "string") return undefined;
		return tmp;
	}, [errors, name]);
	return (
		<InputRow name={name} {...other}>
			<RB.Row noGutters>
				<RB.Col>
					<RB.Form.Select
						value={values.from ? values.from.toString() : ""}
						onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
							onChangeSelect("from", e.target.value);
						}}
						isInvalid={!!myError && !!myError["from"]}
					>
						<option></option>
						{fromOptions.map(({ value, label }) => {
							return (
								<option key={value} value={value}>
									{label}
								</option>
							);
						})}
					</RB.Form.Select>
					{!!myError && !!myError["from"] && (
						<Feedback type={"invalid"} style={{ display: "block" }}>
							{getErrorMessage("form", myError)}
						</Feedback>
					)}
				</RB.Col>
				<RB.Col xs="auto">　～　</RB.Col>
				<RB.Col>
					<RB.Form.Select
						value={values.to ? values.to.toString() : ""}
						onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
							onChangeSelect("to", e.target.value);
						}}
						isInvalid={!!myError && !!myError["to"]}
					>
						<option></option>
						{toFilterOption.map(({ value, label }) => {
							return (
								<option key={value} value={value}>
									{label}
								</option>
							);
						})}
					</RB.Form.Select>
					{!!myError && !!myError["to"] && (
						<Feedback type={"invalid"} style={{ display: "block" }}>
							{getErrorMessage("to", myError)}
						</Feedback>
					)}
				</RB.Col>
			</RB.Row>
		</InputRow>
	);
};
